import { CheckBoxOutlineBlank, CloseRounded, Search } from '@mui/icons-material'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Grid,
  InputBase,
  TextField,
  Typography
} from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import { styled } from '@mui/material/styles'
import { ChangeEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { IRootState } from '../../../../redux/types'
import useScreenWidth from '../../hooks/useScreenWidth'
import { SaveButton } from './index'

const icon = <CheckBoxOutlineBlank fontSize="small" />
const checkedIcon = <CheckBoxIcon fontSize="small" />

const SearchIcon = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}))

const SearchRoot = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[800],
  '&:hover': {
    backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[200] : theme.palette.grey[700]
  },
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(1),
    width: 'auto'
  }
}))

const InputBaseStyled = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: '12ch',
      '&:focus': {
        width: '20ch'
      }
    }
  }
}))

const ListCountries = styled(Grid)(({ theme }) => ({
  maxHeight: '400px',
  overflowY: 'auto',
  padding: theme.spacing(0, 2)
}))

const InputSelectAll = styled(FormControlLabel)(() => ({
  marginLeft: 0
}))

const ContainerForm = styled('div')(({ theme }) => ({
  '& .MuiDialogTitle-root': {
    borderBottom: `1px solid ${theme.palette.divider}`
  }
}))

const BodyForm = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(2)
}))

interface ICountry {
  id: number
  code: string
  name: string
}

type FormElement = ChangeEvent<HTMLInputElement>

function InputCountry({ value: countriesSelected, handleInputChange, userSelected = [], label = null, required = false }: any) {
  const { t } = useTranslation(['global'])
  const isMobile = useScreenWidth(700)

  const { user: authUser } = useSelector((state: IRootState) => state.auth)
  const { countries } = useSelector((state: IRootState) => state.country)
  const { errorForm, msgForm } = useSelector((state: IRootState) => state.validator)

  const [open, setOpen] = useState(false)

  useEffect(() => {
    setCountriesFiltered(countries)
  }, [countries])

  const [countriesFiltered, setCountriesFiltered] = useState<ICountry[]>(countries)

  const handleCheckAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target
    let allCountriesIds: number[] = []
    if (checked) allCountriesIds = authUser.countries

    handleInputChange('countries', allCountriesIds)
  }

  const handleMultiAutoCompleteChange = () => (_: any, value: any) => {
    let optionsMulti: number[] = []
    if (value !== null) {
      for (let i = 0; i < value.length; i++) {
        optionsMulti.push(value[i].id)
      }
    }

    handleInputChange('countries', optionsMulti)
  }

  const handleCheckChange = (event: FormElement) => {
    let { value, checked } = event.target
    const checkValue = Number.parseInt(value)
    let countries = [...countriesSelected]

    // Add or delete country
    if (checked) countries.push(checkValue)
    else countries = countries.filter((country_id: number) => country_id !== checkValue)

    handleInputChange('countries', countries)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const onOpenAutocomplete = (_event: object) => {
    setOpen(true)
  }

  const handleSearch = (event: any) => {
    let countriesFiltered: ICountry[] = [...countries]
    const value = event.target.value

    countriesFiltered = countriesFiltered.filter((country: ICountry) => country.name.toLowerCase().includes(value.toLowerCase()))

    setCountriesFiltered(countriesFiltered)
  }

  return (
    <>
      {authUser !== null && (
        <Autocomplete
          id="multi-autocomplete-countries"
          data-testid="form-autocomplete-country"
          options={countries}
          value={countries.filter((country: any) => countriesSelected.includes(country.id))}
          onChange={handleMultiAutoCompleteChange()}
          disableCloseOnSelect
          fullWidth
          multiple
          limitTags={0}
          onOpen={onOpenAutocomplete}
          disabled={userSelected !== null ? [3, 4, 6].includes(authUser.role_id) && authUser.id === userSelected.id : false}
          getOptionLabel={(country: ICountry) => (country.id !== undefined ? country.name : '')}
          sx={{ maxHeight: 92, overflowY: 'auto' }}
          renderOption={(props, country: ICountry, { selected }: any) => (
            <li {...props}>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                sx={{ marginRight: 1 }}
                onChange={handleCheckChange}
                checked={countriesSelected.includes(country.id)}
                value={country.id}
              />
              {country.name}
            </li>
          )}
          renderInput={(params: any) => (
            <TextField
              {...params}
              error={errorForm.countries}
              helperText={msgForm.countries}
              variant="filled"
              label={label === null ? t('countries') : label}
              InputLabelProps={{ shrink: true }}
              placeholder={t('selected-countries')}
              required={required}
            />
          )}
        />
      )}

      <Dialog
        id="dialog-countries-container"
        open={open}
        onClose={handleClose}
        fullWidth={true}
        fullScreen={isMobile && true}
        maxWidth={isMobile ? undefined : 'md'}
        sx={{ '& .MuiPaper-root': { minWidth: isMobile ? 'unset' : '800px' } }}
        component={ContainerForm}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="dialog-countries-title">
          <Grid container justifyContent="space-between">
            <Typography variant="h6">
              {t('selected-countries')} | {countriesSelected.length}
            </Typography>
            <Button onClick={handleClose} color="primary" variant="text">
              <CloseRounded />
            </Button>
          </Grid>
        </DialogTitle>

        <BodyForm>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormGroup>
                <Box mb={3} px={4} display="flex" justifyContent="space-between">
                  <InputSelectAll
                    control={<Checkbox data-inputcountry="selectAll" id="chk-all-countries" onChange={handleCheckAllChange} />}
                    label={t('select-all')}
                  />
                  <SearchRoot>
                    <SearchIcon>
                      <Search />
                    </SearchIcon>
                    <InputBaseStyled
                      data-inputcountry="searchCountry"
                      placeholder={`${t('countries')}...`}
                      inputProps={{
                        'aria-label': t('country')
                      }}
                      onChange={handleSearch}
                    />
                  </SearchRoot>
                </Box>
                <ListCountries container justifyContent="space-between" alignItems="center">
                  {countriesFiltered.map((country: ICountry, index: number) => (
                    <Grid key={country.id} item xs={6} md={4}>
                      <FormControlLabel
                        key={index}
                        control={
                          <Checkbox
                            data-inputcountry={country.name}
                            checked={countriesSelected.includes(country.id)}
                            onChange={handleCheckChange}
                            value={country.id}
                          />
                        }
                        label={country.name}
                      />
                    </Grid>
                  ))}
                </ListCountries>
              </FormGroup>
            </Grid>
          </Grid>
        </BodyForm>
        <DialogActions>
          <SaveButton autoFocus id="btn-countries-continue" onClick={handleClose}>
            {t('continue')}
          </SaveButton>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default InputCountry
